import React, {useState} from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  Grid,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { IMG_ETHNIC_IMAGE_1, IMG_ETHNIC_IMAGE_2, IMG_ETHNIC_IMAGE_3 } from './imageConfig';
import { ethnicWearProducts } from './Products';

const SearchDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const getTag = (name) => {
    const n = name.toLowerCase();
    if (n.includes('hoodie') || n.includes('t-shirt') || n.includes('sweatshirt')) return 'Not available';
    return null;
  };

  const handleCategoryClick = (slug, name) => {
    // We now allow clicking even if there's a tag, so the user can see the "Coming Soon" page in CategoryPage.js
    navigate(`/products/${slug}`);
    onClose();
  };

  const handleSearchClick = (term) => {
    navigate(`/search?q=${encodeURIComponent(term)}&resources[type]=product`);
    onClose();
  };

  const handleProductClick = (product) => {
    if (product.isSample) return;
    const slug = product.title
      .toLowerCase()
      .replace(/\s*\|\s*/g, "-")
      .replace(/\s+/g, "-");

    navigate(`/product-description/${slug}`, {
      state: {
        product: product,
        country: null, // We'll handle country inside ProductDescription if null
      },
    });
    onClose();
  };

  const categories = [
    { title: 'KURTA SETS', img: IMG_ETHNIC_IMAGE_1, slug: 'kurta-sets' },
    { title: 'SUIT SETS', img: IMG_ETHNIC_IMAGE_2, slug: 'suit-sets' },
    { title: 'FESTIVE WEAR', img: IMG_ETHNIC_IMAGE_3, slug: 'festive-wear' },
  ];

  const getSearchData = (query) => {
    const q = query.toLowerCase();

    // Default empty state
    let data = {
      suggestions: [],
      collections: [],
      products: []
    };

    if (q.includes('kurta') || q.includes('suit') || q.includes('ethnic') || q.includes('anarkali') || q.includes('festive')) {
      data = {
        suggestions: ['kurta sets', 'suit sets', 'anarkali suits', 'festive wear'],
        collections: [{ label: 'Ready to Wear', slug: 'ready-to-wear' }],
        products: ethnicWearProducts
      };
    } else if (q.includes('classic') || q.includes('timeless')) {
      data = {
        suggestions: ['everyday suits', 'timeless prints', 'occasion wear'],
        collections: [{ label: 'The Suit Edit', slug: 'festive-wear' }],
        products: ethnicWearProducts
      };
    } else {
      data = {
        suggestions: ['kurta sets', 'suit sets', 'anarkali suits', 'festive wear'],
        collections: [{ label: 'Ready to Wear', slug: 'ready-to-wear' }],
        products: ethnicWearProducts
      };
    }

    return data;
  };

  const searchData = getSearchData(searchQuery);

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          backgroundColor: '#fff',
          color: '#000',
        }
      }}
      sx={{ zIndex: 1302 }}
    >
      <Box sx={{ px: { xs: 2, md: 8 }, py: { xs: 2, md: 4 }, boxSizing: 'border-box', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SearchIcon sx={{ color: '#000', mr: 2, fontSize: '28px' }} />
          <InputBase 
            placeholder="Start typing to search" 
            fullWidth
            sx={{ color: '#000', fontSize: '20px', fontWeight: 300 }}
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(`/search?q=${encodeURIComponent(searchQuery)}&resources[type]=product`);
                onClose();
              }
            }}
          />
          <IconButton onClick={onClose} sx={{ color: '#000', ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {searchQuery.trim() === '' ? (
          <>
            <Grid container spacing={4} sx={{ mb: 6 }}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Popular searches</Typography>
                <Box sx={{ display: 'flex', gap: 3, color: "#655f5fff" }}>
                  {[
                    { label: 'Kurta sets', slug: 'kurta-sets' },
                    { label: 'Suit sets', slug: 'suit-sets' },
                    { label: 'Festive wear', slug: 'festive-wear' },
                  ].map(item => {
                    const tag = getTag(item.label);
                    return (
                      <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          onClick={() => handleCategoryClick(item.slug, item.label)}
                          sx={{ 
                            cursor: tag ? 'default' : 'pointer', 
                            fontSize: '14px', 
                            color: tag ? '#999' : 'inherit',
                            '&:hover': { textDecoration: tag ? 'none' : 'underline' } 
                          }}
                        >
                          {item.label}
                        </Typography>
                        {tag && (
                          <Typography sx={{ fontSize: '10px', color: '#888', fontStyle: 'italic' }}>
                            ({tag})
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Useful pages</Typography>
                <Box sx={{ display: 'flex', gap: 3, color: "#655f5fff" }}>
                  {[
                    { label: 'FAQs', slug: 'faq' },
                    { label: 'Returns', slug: 'return-policy' },
                    { label: 'Shipping', slug: 'contact' },
                  ].map(item => (
                    <Typography
                      key={item.label}
                      onClick={() => { navigate(`/${item.slug}`); onClose(); }}
                      sx={{ cursor: 'pointer', fontSize: '14px', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {item.label}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, mx: 0 }}>
              {categories.map((cat) => {
                const tag = getTag(cat.title);
                return (
                  <Box
                    key={cat.title}
                    sx={{ width: { xs: '33.33%', md: '20%' }, px: { xs: 0.5, md: 0 }, mb: { xs: 2, md: 0 }, cursor: tag ? 'default' : 'pointer' }}
                    onClick={() => handleCategoryClick(cat.slug, cat.title)}
                  >
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Box 
                        component="img" 
                        src={cat.img} 
                        alt={cat.title} 
                        sx={{ 
                          width: '100%', 
                          aspectRatio: '3/4', 
                          objectFit: 'cover', 
                          mb: 1, 
                          display: 'block',
                          opacity: tag ? 0.6 : 1
                        }} 
                      />
                      {tag && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            padding: '2px 6px',
                            borderRadius: '2px',
                            backdropFilter: 'blur(4px)'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '8px',
                              fontWeight: 700,
                              color: '#000',
                              letterSpacing: '0.5px'
                            }}
                          >
                            {tag.toUpperCase()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ 
                      fontSize: '11px', 
                      letterSpacing: '1px', 
                      fontWeight: 600, 
                      mt: 2, 
                      textAlign: 'left', 
                      color: tag ? '#888' : '#000',
                      '&:hover': { textDecoration: tag ? 'none' : 'underline' } 
                    }}>
                      {cat.title}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Search suggestions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, color: "#000", mb: 4 }}>
                {searchData.suggestions.map((suggestion, index) => (
                  <Typography key={index} onClick={() => handleSearchClick(suggestion)} sx={{ fontSize: '13px', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    {suggestion}
                  </Typography>
                ))}
              </Box>

              <Typography sx={{ fontWeight: 600, mb: 2, fontSize: '15px' }}>Collection results ({searchData.collections.length})</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, color: "#000" }}>
                {searchData.collections.map((collection, index) => (
                  <Typography key={index} onClick={() => handleCategoryClick(collection.slug)} sx={{ fontSize: '13px', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    {collection.label}
                  </Typography>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>Products ({searchData.products.length} of {searchData.products.length < 30 ? searchData.products.length : 30})</Typography>
                <Typography onClick={() => { navigate(`/search?q=${encodeURIComponent(searchQuery)}&resources[type]=product`); onClose(); }} sx={{ fontWeight: 600, fontSize: '12px', letterSpacing: '1px', cursor: 'pointer' }}>VIEW ALL RESULTS</Typography>
              </Box>
              <Grid container spacing={1} sx={{ mx: -0.5 }}>
                {searchData.products.map((prod, idx) => (
                  <Grid item xs={6} md={2} key={idx} sx={{ px: 0.5 }}>
                    <Box 
                      onClick={() => handleProductClick(prod)}
                      sx={{ 
                        cursor: prod.isSample ? 'default' : 'pointer',
                        '&:hover img': { opacity: prod.isSample ? 1 : 0.9 }
                      }}
                    >
                      <Box sx={{ position: 'relative', pt: '125%', bgcolor: '#f4f4f4', mb: 1.5 }}>
                        <Box 
                          component="img" 
                          src={prod.images?.[0] || prod.img}
                          alt={prod.title}
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            transition: 'opacity 0.2s'
                          }} 
                        />
                      </Box>
                      <Typography sx={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        mb: 0.5, 
                        lineHeight: 1.3,
                        '&:hover': { textDecoration: 'underline' }
                      }}>
                        {prod.title?.replace(/\|/g, "")}
                      </Typography>
                    </Box>
                    {prod.oldPrice ? (
                      <Box sx={{ display: 'flex', gap: 1, fontSize: '11px' }}>
                        <Typography sx={{ color: 'red', fontWeight: 600, fontSize: '11px' }}>{prod.price}</Typography>
                        <Typography sx={{ textDecoration: 'line-through', color: '#888', fontSize: '11px' }}>{prod.oldPrice}</Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '11px', color: '#000' }}>
                        {prod.isSample ? 'Sample look · not for sale' : prod.price}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </Drawer>
  );
};

export default SearchDrawer;
